import React, { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Building,
  LogOut,
  Shield,
  Settings,
  Sparkles,
  ShoppingBag,
  Bell,
  CheckCircle2,
  AlertCircle,
  User,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Logo } from '../ui/Logo'
import { ThemeToggle } from '../ui/ThemeToggle'
import toast from 'react-hot-toast'

export const PlatformAdminLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuth()
  const [activityMenuOpen, setActivityMenuOpen] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const activityRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    logout()
    navigate('/')
    toast.success('Logged out successfully')
  }

  const getInitials = () => {
    if (!user) return 'AD'
    const firstInitial = user.firstName?.[0] || ''
    const lastInitial = user.lastName?.[0] || ''
    const initials = (firstInitial + lastInitial).toUpperCase()
    return initials || user.email?.[0]?.toUpperCase() || 'AD'
  }

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activityRef.current && !activityRef.current.contains(event.target as Node)) {
        setActivityMenuOpen(false)
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Mock admin activities/notifications
  const allActivities = [
    {
      id: 1,
      type: 'urgent',
      title: 'Pending User Verifications',
      description: '5 users require identity verification review',
      action: () => {
        setActivityMenuOpen(false)
        navigate('/app/admin/users')
      },
      urgent: true,
      priority: 1
    },
    {
      id: 2,
      type: 'warning',
      title: 'Exchange Deadline Approaching',
      description: '3 exchanges are within 10 days of their 45-day deadline',
      action: () => {
        setActivityMenuOpen(false)
        navigate('/app/admin/exchanges')
      },
      urgent: true,
      priority: 2
    },
    {
      id: 3,
      type: 'info',
      title: 'Document Review Required',
      description: '12 documents pending admin approval',
      action: () => {
        setActivityMenuOpen(false)
        navigate('/app/admin/documents')
      },
      urgent: false,
      priority: 3
    },
    {
      id: 4,
      type: 'info',
      title: 'System Update Available',
      description: 'New workflow features available in Vectr integrations',
      action: () => {
        setActivityMenuOpen(false)
        navigate('/app/admin/integrations')
      },
      urgent: false,
      priority: 4
    },
    {
      id: 5,
      type: 'reminder',
      title: 'Monthly Report Due',
      description: 'Generate and review monthly admin report',
      action: () => {
        setActivityMenuOpen(false)
        navigate('/app/admin')
      },
      urgent: false,
      priority: 5
    }
  ]

  const activities = allActivities.sort((a, b) => {
    if (a.urgent && !b.urgent) return -1
    if (!a.urgent && b.urgent) return 1
    return a.priority - b.priority
  })

  const urgentCount = activities.filter(a => a.urgent).length
  const totalCount = activities.length
  const nextTask = activities[0]

  const adminNavItems = [
    { path: '/app', label: 'Dashboard', icon: LayoutDashboard, iconOnly: false },
    { path: '/app/admin/exchanges', label: 'Exchanges', icon: Sparkles, iconOnly: false },
    { path: '/app/admin/marketplace', label: 'Marketplace', icon: ShoppingBag, iconOnly: false },
    { path: '/app/admin/orgs', label: 'Orgs', icon: Building, iconOnly: false },
    { path: '/app/admin/users', label: 'Users', icon: Users, iconOnly: false },
    { path: '/app/admin/settings', label: 'Settings', icon: Settings, iconOnly: true },
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden flex flex-col transition-colors duration-300">
      {/* Admin Header */}
      <div className="px-4 sm:px-6 lg:px-8 pt-8 sticky top-0 z-40 flex-shrink-0">
        <nav className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-visible">
          <div className="px-4 md:px-6 overflow-visible rounded-[28px]">
            <div className="flex items-center h-20 gap-3 md:gap-4 lg:gap-6">
              {/* Logo */}
              <Link to="/app" className="flex items-center space-x-2 flex-shrink-0 group mr-2 md:mr-4">
                <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
              </Link>

              {/* Admin Navigation */}
              <div className="flex items-center gap-1 md:gap-2 flex-1 justify-center px-2 md:px-4 min-w-0 mr-2 md:mr-4">
                <div className="flex items-center gap-1 md:gap-2 justify-center lg:justify-start overflow-x-auto scrollbar-hide w-full" style={{ flexWrap: 'nowrap' }}>
                  {adminNavItems.map((item) => {
                    const IconComponent = item.icon
                    const isActive = location.pathname === item.path ||
                      (item.path !== '/app' && location.pathname.startsWith(item.path)) ||
                      (item.path === '/app' && location.pathname === '/app' && !location.pathname.startsWith('/app/admin'))
                    const isDashboard = item.path === '/app'
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group relative flex items-center gap-1.5 md:gap-2.5 ${isDashboard ? 'pl-0 pr-2 md:pr-3 lg:pr-4' : 'px-2 md:px-3 lg:px-4'} py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap ${
                          isActive
                            ? 'text-[#69adba] dark:text-primary-400 font-bold underline decoration-2 underline-offset-4'
                            : 'text-slate-700 dark:text-slate-300 hover:text-[#69adba] dark:hover:text-primary-400 font-medium'
                        }`}
                        style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}
                        title={item.label}
                      >
                        {!isActive && (
                          <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        )}
                        {IconComponent && (
                          <IconComponent 
                            className={`h-5 w-5 relative z-10 flex-shrink-0 ${
                              isActive 
                                ? 'text-[#69adba] dark:text-primary-400' 
                                : 'text-slate-600 dark:text-slate-400'
                            }`}
                            style={{ display: 'block' }}
                          />
                        )}
                        {!item.iconOnly && (
                          <span className={`text-sm relative z-10 ${isActive ? 'text-[#69adba] dark:text-primary-400 font-bold' : ''}`}>{item.label}</span>
                        )}
                        <div
                          className={`${item.iconOnly ? '' : 'md:hidden '}absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50`}
                        >
                          {item.label}
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </div>

              {/* Right side - Notifications, Theme Toggle, Profile & Logout */}
              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 min-w-fit">
                {/* Activity/Notifications Bell */}
                <div className="relative" ref={activityRef} style={{ zIndex: 10001 }}>
                  <button
                    onClick={() => setActivityMenuOpen(!activityMenuOpen)}
                    className={`group relative flex items-center justify-center p-2 rounded-2xl transition-colors duration-300 hover:bg-slate-50 dark:hover:bg-slate-700/40 hover:backdrop-blur-xl ${
                      totalCount > 0 ? 'animate-bounce-up-down' : ''
                    }`}
                  >
                    <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    {totalCount > 0 && (
                      <span className={`absolute -top-1 -right-1 h-5 w-5 text-white text-xs font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-800 ${
                        urgentCount > 0 
                          ? 'bg-red-500 animate-pulse' 
                          : 'bg-[#69adba] dark:bg-[#69adba]'
                      }`}>
                        {totalCount}
                      </span>
                    )}
                  </button>

                  {/* Activity Dropdown */}
                  {activityMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-80 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
                        {/* Header */}
                        <div className="px-5 py-4 bg-gradient-to-br from-slate-50 dark:from-slate-700/50 to-transparent border-b border-white/50 dark:border-slate-700/50">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                              <Bell className="h-4 w-4 text-[#69adba] dark:text-[#69adba]" />
                              Admin Tasks & Notifications
                            </h3>
                            {activities.length > 0 && (
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                urgentCount > 0 
                                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                  : 'bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba] dark:text-[#69adba]'
                              }`}>
                                {activities.length} task{activities.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {nextTask && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-slate-600 dark:text-slate-400">Next:</span>
                              <span className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                {nextTask.title}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Activity List */}
                        <div className="max-h-96 overflow-y-auto">
                          {activities.length > 0 ? (
                            <div className="p-2">
                              {activities.map((activity, index) => (
                                <button
                                  key={activity.id}
                                  onClick={activity.action}
                                  className={`w-full text-left p-3 rounded-2xl hover:bg-white/60 dark:hover:bg-slate-700/60 transition-all duration-200 group relative ${
                                    index === 0 ? 'ring-2 ring-[#69adba] dark:ring-[#69adba] ring-offset-2 dark:ring-offset-slate-800' : ''
                                  }`}
                                >
                                  {index === 0 && (
                                    <div className="absolute -left-1 -top-1 bg-[#69adba] dark:bg-[#69adba] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                      NEXT
                                    </div>
                                  )}
                                  <div className="flex items-start gap-3">
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
                                      activity.urgent 
                                        ? 'bg-red-100 dark:bg-red-900/30' 
                                        : 'bg-[#69adba]/10 dark:bg-[#69adba]/20'
                                    }`}>
                                      {activity.urgent ? (
                                        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                      ) : (
                                        <CheckCircle2 className="h-4 w-4 text-[#69adba] dark:text-[#69adba]" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                          {activity.title}
                                        </p>
                                        {activity.urgent && (
                                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                                            URGENT
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-600 dark:text-slate-400">
                                        {activity.description}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="p-8 text-center">
                              <CheckCircle2 className="h-12 w-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">All caught up!</p>
                              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                No pending tasks or notifications
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <ThemeToggle />

                {/* Profile Menu */}
                <div className="relative" ref={profileRef} style={{ zIndex: 10001 }}>
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="group relative flex items-center px-2 lg:px-3 py-2 rounded-2xl transition-all duration-300 hover:bg-white/40 dark:hover:bg-slate-700/40 hover:backdrop-blur-xl"
                  >
                    {/* Avatar Circle */}
                    <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 dark:from-slate-600 dark:via-slate-700 dark:to-slate-800 flex items-center justify-center text-white font-bold text-xs lg:text-sm shadow-xl ring-2 lg:ring-4 ring-white/50 dark:ring-slate-700/50 group-hover:ring-white/70 dark:group-hover:ring-slate-600/70 group-hover:shadow-2xl transition-all duration-300">
                      {getInitials()}
                    </div>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {profileMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-64 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
                        {/* User Info */}
                        <div className="px-5 py-4 bg-gradient-to-br from-slate-50 dark:from-slate-700/50 to-transparent border-b border-white/50 dark:border-slate-700/50">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 dark:from-slate-600 dark:via-slate-700 dark:to-slate-800 flex items-center justify-center text-white font-bold shadow-xl">
                              {getInitials()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-2">
                                <User className="h-4 w-4 text-[#69adba] dark:text-[#69adba]" />
                                {user?.firstName} {user?.lastName}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {user?.email}
                              </p>
                              <p className="text-[10px] text-red-600 dark:text-red-400 font-semibold mt-0.5 flex items-center gap-1">
                                <Shield className="h-3 w-3" />
                                Admin
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <div className="p-2">
                          <Link
                            to="/app/profile"
                            onClick={() => setProfileMenuOpen(false)}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 hover:backdrop-blur-xl transition-all duration-200"
                          >
                            <div className="w-8 h-8 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center group-hover:bg-[#69adba]/20 dark:group-hover:bg-[#69adba]/30 transition-colors">
                              <User className="h-4 w-4 text-[#69adba] dark:text-[#69adba]" />
                            </div>
                            <span className="font-medium">Profile Settings</span>
                          </Link>

                          <button
                            onClick={() => {
                              setProfileMenuOpen(false)
                              handleLogout()
                            }}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200 w-full mt-2"
                          >
                            <div className="w-8 h-8 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-800/50 transition-colors">
                              <LogOut className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                            <span className="font-medium">Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8 relative z-10 flex-grow">
        <main className="max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="px-4 sm:px-6 lg:px-8 pb-8 relative z-0 mb-4 mt-auto flex-shrink-0">
        <div className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Logo className="h-10 w-auto" />
              <div className="text-center md:text-left">
                <p className="text-slate-900 dark:text-slate-100 text-sm font-bold">Open1031 Admin Portal</p>
                <p className="text-slate-600 dark:text-slate-400 text-xs mt-1">
                  Copyright &copy; {new Date().getFullYear()} All Rights Reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export const AdminLayout = PlatformAdminLayout
